import { useRecurly } from "@recurly/react-recurly";
import { t } from "i18next";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Footer from "../../../components/Footer";
import MobileViewLayout from "../../../layouts/MobileViewLayout";
import ApiService from "../../../service/api";
import { showErrorToast, showSuccessToast } from "../../../utils/ToastsModal";
import { addS3Url, generateRandomCode } from "../../../utils/modules";
import { Loader } from "../../../widgets/Loader";
import Timer from "../../../widgets/Timer";
import GatewayFeatures from "./GatewayFeatures";
import PayPalElement from "./PayPalElement";
import Transformation from "./Transformation";
import TryFreeLanz from "./TryFreeLanz";

const Payment = () => {
  const navigate = useNavigate();
  const recurly = useRecurly();
  const risk = recurly.Risk();
  const location = useLocation();

  const email = new URLSearchParams(location.search).get("email");
  const stateEmail = email || location?.state?.email;
  const currentYear = new Date().getFullYear().toString().slice(2, 4);

  const formRef = useRef();
  const emailRef = useRef();
  const cardNumberRef = useRef();
  const cardMonthRef = useRef();
  const cardYearRef = useRef();
  const cardCvvRef = useRef();

  const [tab, setTab] = useState(false);
  const [currentPlan, setCurrentPlan] = useState("1-month");
  const [paypalPlan, setPaypalPlan] = useState("");
  const [unitPrice, setUnitPrice] = useState(0);
  const [cardNumber, setCardNumber] = useState("");
  const [cardMonth, setCardMonth] = useState("");
  const [cardYear, setCardYear] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [cardNumberError, setCardNumberError] = useState(false);
  const [cardMonthError, setCardMonthError] = useState(false);
  const [cardYearError, setCardYearError] = useState(false);
  const [cardCvvError, setCardCvvError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isSupport, setIsSupport] = useState(true);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [isPaymentProcessing, setPaymentProcessing] = useState(false);
  const [appleAvailable, setAppleAvailable] = useState("");
  const [windowWidth, setWindowWidth] = useState(0);
  const [gender, setGender] = useState(location?.state?.gender);
  const [maxPrice, setMaxPrice] = useState(0);

  const nameString = stateEmail?.split("@")[0];
  const divideStringIntoHalf = () => {
    const length = nameString?.length;
    const middle = Math.floor(length / 2);

    const firstHalf = nameString?.slice(0, middle);
    setFirstName(firstHalf);
    const secondHalf = nameString?.slice(middle);
    setLastName(secondHalf);
  };

  useEffect(() => {
    !stateEmail && navigate(`/${location?.state?.gender}/emailForm`);
    divideStringIntoHalf();
  }, [stateEmail]);

  const searchParams = new URLSearchParams(location.search);

  const checkEmail = async () => {
    if (searchParams.get("email")) {
      emailRef.current.value = searchParams.get("email");

      await ApiService.getSubscribers({
        api_secret: process.env.REACT_APP_CK_SECRET_KEY,
        email_address: searchParams.get("email"),
      })
        .then((res) => {
          if (res.data.subscribers.length > 0) {
            const data = res.data.subscribers[0];
            setGender(data.first_name);
          }
        })
        .catch((err) => console.log(err));
    }
  };

  useEffect(() => {
    checkEmail();
  }, []);

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    const fetchPlans = async () => {
      await ApiService.getRecurlyPrice().then(async (response) => {
        if (response.status === 200) {
          const priceList = response?.data?.data?.plan_list;
          const currentUnitPrice =
            priceList?.length > 0 &&
            priceList?.find((plans) => plans.plan_code === currentPlan);
          setUnitPrice(currentUnitPrice?.unit_amount);
          setPaypalPlan(currentUnitPrice?.paypal_code);
          setCurrentPlan(currentUnitPrice?.plan_code);
          setMaxPrice(currentUnitPrice?.max_amount);
        }
      });
    };
    fetchPlans();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleTags = () => {
    ApiService.removeTag("4643341", {
      api_secret: process.env.REACT_APP_CK_SECRET_KEY,
      email: stateEmail.toLowerCase(),
    }).then(() => {
      ApiService.addTag("4643340", {
        api_secret: process.env.REACT_APP_CK_SECRET_KEY,
        email: stateEmail.toLowerCase(),
      });
    });
  };

  const handleCardNumberChange = (e) => {
    const inputVal = e.target.value;
    const inputCardNumber = e.target.value.replace(/\D/g, "");
    const formattedCardNumber = inputCardNumber
      .replace(/(\d{4})/g, "$1 ")
      .trim();
    setCardNumber(formattedCardNumber);
    const checkVal = inputVal.split(" ").join("");
    const length = checkVal.length;

    !inputVal || length < 15 || length > 16
      ? setCardNumberError(true)
      : setCardNumberError(false);

    if (length === 16) {
      cardMonthRef.current.focus({ preventScroll: true });
    }
  };

  const handleCardMonthChange = (e) => {
    const length = e.target.value.length;
    setCardMonth(e.target.value);
    length < 2 || e.target.value > 12
      ? setCardMonthError(true)
      : setCardMonthError(false);
    if (length === 2 && e.target.value <= 12) {
      cardYearRef.current.focus({ preventScroll: true });
    }
  };

  const handleCardYearChange = (e) => {
    const length = e.target.value.length;
    setCardYear(e.target.value);
    length < 2 || e.target.value < currentYear
      ? setCardYearError(true)
      : setCardYearError(false);
    if (length === 2 && e.target.value >= currentYear) {
      cardCvvRef.current.focus({ preventScroll: true });
    }
  };

  const handleCardCvvChange = (e) => {
    const length = e.target.value.length;
    setCardCvv(e.target.value);
    length < 3 ? setCardCvvError(true) : setCardCvvError(false);
    if (length === 3) {
      setCardCvvError(false);
    }
  };

  const handleThreeDSPayment = async (formData) => {
    setLoading(true);
    try {
      await ApiService.createSubscription(formData)
        .then(async (response) => {
          if (response.status === 200) {
            await ApiService.loginUser({
              email: stateEmail.toLowerCase(),
            })
              .then((res) => {
                if (res.status === 200) {
                  handleTags();
                  localStorage.setItem("email", stateEmail.toLowerCase());
                  showSuccessToast(res?.data?.message);
                  navigate(`/otp`);
                  setLoading(false);
                }
              })
              .catch((err) => {
                showErrorToast(err?.response?.data);
                setLoading(false);
              });
          }
        })
        .catch((error) => {
          showErrorToast(
            error?.response?.data?.error ??
              error?.response?.data?.message ??
              error?.response?.data ??
              "Something went wrong"
          );
          setLoading(false);
        });
    } catch (e) {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    let showErr = false;
    if (!recurly && showErr) {
      return;
    }
    let formData = new FormData();
    formData.append("first_name", firstName);
    formData.append("last_name", lastName);
    formData.append("number", cardNumber);
    formData.append("month", cardMonth);
    formData.append("year", cardYear);
    formData.append("cvv", cardCvv);
    formData.append("email", stateEmail);
    formData.append("three_d_secure_action_required", true);
    formData.append("key", "ewr1-5oFEJbtYWURbdeA3z2DHmJ");

    await ApiService.getRecurlyToken(formData)
      .then((response) => {
        if (
          !cardNumberError &&
          !cardYearError &&
          !cardMonthError &&
          !cardCvvError &&
          !response.message
        ) {
          setLoading(true);
          const code = generateRandomCode();
          const token = response?.data?.id;
          if (token) {
            try {
              ApiService.createSubscription({
                email: stateEmail.toLowerCase(),
                token: token,
                code: code,
                plan_code: currentPlan,
                payment_type: "recurly",
              })
                .then(async (response) => {
                  if (response.status === 201) {
                    setLoading(true);
                    const threeDSecure = risk.ThreeDSecure({
                      actionTokenId: response?.data?.action_token_id,
                    });
                    threeDSecure.on("error", (err) => {
                      setLoading(false);
                      console.log("threeDSecure err", err);
                      showErrorToast("Payment Rejected");
                    });

                    threeDSecure.on("token", (threeDsToken) => {
                      setLoading(true);
                      handleThreeDSPayment({
                        email: stateEmail.toLowerCase(),
                        token: token,
                        plan_code: currentPlan,
                        three_d_secure_action_result_token_id: threeDsToken?.id,
                        code: code,
                        accountCode: response?.data?.accountCode,
                        accountId: response?.data?.accountId,
                        payment_type: "recurly",
                      });
                    });

                    threeDSecure.attach(document.getElementById("3D-Secure"));
                  }
                  if (response.status === 200) {
                    await ApiService.loginUser({
                      email: stateEmail.toLowerCase(),
                    })
                      .then((res) => {
                        if (res.status === 200) {
                          handleTags();
                          localStorage.setItem("email", stateEmail);
                          showSuccessToast(res?.data?.message);
                          navigate(`/otp`);
                          setLoading(false);
                        }
                      })
                      .catch((err) => {
                        showErrorToast(err?.response?.data);
                        setLoading(false);
                      });
                  }
                })
                .catch((error) => {
                  showErrorToast(
                    error?.response?.data?.error ??
                      error?.response?.data?.message ??
                      error?.response?.data ??
                      "Something went wrong"
                  );
                  setLoading(false);
                });
            } catch (e) {
              setLoading(false);
              console.log(e.err);
            }
          }
        } else {
          if (
            response?.message ||
            (!cardNumberError &&
              !cardYearError &&
              !cardMonthError &&
              !cardCvvError)
          ) {
            showErrorToast("Card number cannot be a test card number");
          }
          setLoading(false);
        }
      })
      .catch((error) => console.log("error", error));
  };

  useEffect(() => {
    const applePayOptions = {
      requiredBillingAddressFields: ["all"],
      requiredShippingContactFields: ["phone", "email", "postalAddress"],
      countryCode: "US",
      recurringPaymentRequest: true,
    };

    recurly.configure({
      applePay: applePayOptions,
    });
  }, [recurly]);

  useEffect(() => {
    if (!window.ApplePaySession) {
      if (appleAvailable !== "Not Available") {
        setIsSupport(false);
        setAppleAvailable("Not Available");
      }
      return;
    }
  }, [isPaymentProcessing]);

  const sendData = async (token) => {
    setLoadingPayment(true);
    const code = generateRandomCode();

    try {
      await ApiService.createSubscription({
        email: stateEmail.toLowerCase(),
        token: token.id,
        code: code,
        plan_code: currentPlan,
        payment_type: "recurly",
      })
        .then(async (response) => {
          if (response.status === 200) {
            await ApiService.loginUser({
              email: stateEmail.toLowerCase(),
            })
              .then((res) => {
                if (res.status === 200) {
                  handleTags();
                  localStorage.setItem("email", stateEmail);
                  showSuccessToast(res?.data?.message);
                  navigate(`/otp`);
                  setLoadingPayment(false);
                }
              })
              .catch((err) => {
                showErrorToast(err?.response?.data);
                setLoadingPayment(false);
              });
          }
        })
        .catch((error) => {
          showErrorToast(error?.response?.data);
          setLoadingPayment(false);
        });
    } catch (e) {
      setLoadingPayment(false);
    }
    setPaymentProcessing(false);
    setLoadingPayment(false);
  };

  // const applePay = recurly.ApplePay({
  //   country: "US",
  //   currency: "USD",
  //   label: "FreeLanz.app",
  //   total: `${unitPrice}`,
  // });

  const endOfTrial = new Date(
    Date.now() + 7 * 24 * 60 * 60 * 1000
  ).toISOString();

  const applePay = recurly.ApplePay({
    country: "US",
    currency: "USD",
    paymentRequest: {
      total: { label: "1-Week Trial", amount: "1.00" },
      recurringPaymentRequest: {
        paymentDescription: "Monthly Subscription",
        regularBilling: {
          label: "Monthly",
          amount: `${maxPrice}`,
          paymentTiming: "recurring",
          recurringPaymentIntervalUnit: "month",
          recurringPaymentIntervalCount: 1,
          recurringPaymentStartDate: endOfTrial,
        },
        trialBilling: {
          label: "1-Week Trial",
          amount: `${unitPrice}`,
          paymentTiming: "recurring",
          recurringPaymentIntervalUnit: "day",
          recurringPaymentIntervalCount: 7,
          recurringPaymentEndDate: endOfTrial,
        },
      },
    },
  });

  const handleAppleClick = (event) => {
    event.preventDefault();
    setTab(false);
    if (!isSupport) {
      showErrorToast(
        "Your device or browser is not compatible with Apple Pay."
      );
      return;
    } else if (isSupport && applePay && stateEmail) {
      applePay?.ready(function () {
        applePay?.begin();
      });

      applePay.on("error", function (err) {
        console.error("Apple Pay error", err);
      });

      applePay.on("token", async function (token) {
        await sendData(token);
      });
    }
  };

  const handleOpen = (event) => {
    event.preventDefault();
    setTab(true);
  };

  return (
    <>
      <MobileViewLayout>
        <>
          <Timer />
          <div
            className="flex-grow-1"
            style={{
              paddingTop: "75px",
            }}
          >
            <div className="px-3">
              <div className="d-flex flex-column align-items-center text-center mt-4">
                <Transformation gender={gender} />
                <p
                  className="mt-4"
                  style={{ color: "#989898", fontSize: "12px" }}
                >
                  This is not a guarantee or promise of results.
                </p>
                <TryFreeLanz />
                <h3
                  className="m-0 p-0 mt-3 mb-4 color-primary"
                  style={{ fontWeight: "bold" }}
                >
                  {t("payment.trial")}
                </h3>
                {/* <h5
                  className="m-0 p-0 mt-2 mb-4 color-primary"
                  style={{ fontWeight: "bold" }}
                >
                  <span
                    className=""
                    style={{
                      color: "#989898",
                      fontWeight: 600,
                      fontSize: "17px",
                    }}
                  >
                    <del>$39.99</del>
                  </span>{" "}
                  $9.99
                </h5> */}
                <form
                  onSubmit={handleSubmit}
                  ref={formRef}
                  className="stripe-form w-100"
                >
                  <div className="text-start mt-3" style={{ display: "none" }}>
                    <p
                      className="m-0 text-14"
                      style={{ color: "#6C6E79", fontWeight: 600 }}
                    >
                      EMAIL
                    </p>
                    <input
                      ref={emailRef}
                      className={` input-field ${
                        emailError ? "border-error" : "border-green"
                      } w-100 mt-1 p-2`}
                      id="email"
                      name="email"
                      type="text"
                      placeholder=""
                      required
                      defaultValue={stateEmail}
                      data-recurly={"email"}
                      style={{ textTransform: "lowercase" }}
                    />
                    {emailError ? (
                      <p className="small" style={{ color: "red" }}>
                        Enter valid email address
                      </p>
                    ) : null}
                  </div>
                  {tab && (
                    <div className={`mt-2 mb-3 card-container`}>
                      <div className="text-start">
                        <p className="m-0 text-14 card-label">CARD NUMBER</p>
                        <input
                          ref={cardNumberRef}
                          className={` input-field ${
                            cardNumberError ? "border-error" : "border-green"
                          } w-100 mt-1 p-2 bg-white`}
                          id="cardNumber"
                          name="cardNumber"
                          type="text"
                          placeholder="XXXX XXXX XXXX XXXX"
                          required
                          value={cardNumber}
                          data-recurly={"number"}
                          onChange={(event) => handleCardNumberChange(event)}
                          inputMode="numeric"
                          autoComplete="off"
                          style={{ fontFamily: "Helvetica" }}
                        />
                      </div>

                      <div className="d-flex mt-4 justify-content-between">
                        <div
                          className="text-start"
                          style={{ minWidth: "33%", width: "55%" }}
                        >
                          <p className="m-0 text-14 card-label">EXPIRES ON</p>

                          <div className="d-flex">
                            <input
                              ref={cardMonthRef}
                              className={` input-field ${
                                cardMonthError ? "border-error" : "border-green"
                              } w-50 mt-1 p-2 bg-white me-1`}
                              id="cardMonth"
                              name="cardMonth"
                              type="text"
                              placeholder="MM"
                              required
                              value={cardMonth}
                              data-recurly={"month"}
                              onChange={(event) => handleCardMonthChange(event)}
                              inputMode="numeric"
                              autoComplete="off"
                              style={{ fontFamily: "Helvetica" }}
                              maxLength={2}
                            />
                            <input
                              ref={cardYearRef}
                              className={` input-field ${
                                cardYearError ? "border-error" : "border-green"
                              } w-50 mt-1 p-2 bg-white ms-1`}
                              id="cardYear"
                              name="cardYear"
                              type="text"
                              placeholder="YY"
                              required
                              value={cardYear}
                              data-recurly={"year"}
                              onChange={(event) => handleCardYearChange(event)}
                              inputMode="numeric"
                              autoComplete="off"
                              style={{ fontFamily: "Helvetica" }}
                              maxLength={2}
                            />
                          </div>
                        </div>
                        <div className="text-start" style={{ width: "30%" }}>
                          <p className="m-0 text-14 card-label">CVV</p>
                          <input
                            ref={cardCvvRef}
                            className={` input-field ${
                              cardCvvError ? "border-error" : "border-green"
                            } w-100 mt-1 p-2 bg-white`}
                            id="cardYear"
                            name="cardYear"
                            type="text"
                            placeholder="CVV"
                            required
                            value={cardCvv}
                            data-recurly={"cvv"}
                            onChange={(event) => handleCardCvvChange(event)}
                            inputMode="numeric"
                            autoComplete="off"
                            style={{ fontFamily: "Helvetica" }}
                            maxLength={4}
                          />
                        </div>
                        <input
                          type="hidden"
                          name="recurly-token"
                          data-recurly="token"
                        />
                      </div>
                    </div>
                  )}
                  {!tab ? (
                    <button
                      className="w-100 main-button fw-bold fs-5 text-white mt-1 mb-4"
                      style={{ height: "55px", backgroundColor: "#009B24" }}
                      onClick={handleOpen}
                    >
                      Credit<span style={{ fontWeight: "300" }}>/</span>
                      Debit Card
                    </button>
                  ) : isLoading ? (
                    <Loader className="align-self-center mb-4" />
                  ) : (
                    <button
                      type="submit"
                      className="w-100 main-button py-3 fw-bold fs-5 text-white mb-4"
                      style={{ height: "55px", backgroundColor: "#009B24" }}
                    >
                      Process Card
                    </button>
                  )}
                  {loadingPayment ? (
                    <Loader className="align-self-center mb-4" />
                  ) : (
                    <button
                      id="apple-pay"
                      onClick={handleAppleClick}
                      className="w-100 main-button d-flex justify-content-center align-items-center fs-5 text-white mb-4 custom-payment-btn"
                    >
                      <img
                        src={addS3Url("/payment/apple-pay.svg")}
                        alt=""
                        height={33}
                      />
                    </button>
                  )}

                  {/* <PayPalElement email={stateEmail} plan={paypalPlan} /> */}
                </form>
                <div
                  id="3D-Secure"
                  className="threeDSecure"
                  style={{
                    maxWidth: "390px",
                  }}
                ></div>
                <GatewayFeatures />
              </div>
            </div>
          </div>
        </>
      </MobileViewLayout>
      <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-end pt-2 px-0 pb-2 ">
        <hr style={{ width: "100%" }} />
        <Footer />
      </div>
    </>
  );
};

export default Payment;
